/**
 * Authentication Context - Google OAuth
 * ======================================
 * Professional Google OAuth implementation without Firebase Auth dependency.
 * Uses @react-oauth/google for frontend and JWT sessions for backend.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiService, setAuthContext } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Storage keys
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentToken, setCurrentToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // User data management
  const [userData, setUserData] = useState(null);
  const [userDataLoading, setUserDataLoading] = useState(false);
  const [userDataError, setUserDataError] = useState(null);

  /**
   * Initialize auth state from localStorage
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedToken = localStorage.getItem(TOKEN_KEY);
        const savedUser = localStorage.getItem(USER_KEY);

        if (savedToken && savedUser) {
          setCurrentToken(savedToken);
          setCurrentUser(JSON.parse(savedUser));
          
          // Verify token is still valid
          try {
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
              {
                headers: { 'Authorization': `Bearer ${savedToken}` }
              }
            );
            
            if (response.ok) {
              const data = await response.json();
              setCurrentUser(data.user);
              setUserData({
                subscription: data.user.subscription,
                credits: data.user.z_credits,
                profile: {
                  name: data.user.name,
                  email: data.user.email,
                  picture: data.user.picture,
                  familyName: data.user.familyName,
                  familyDescription: data.user.familyDescription,
                  profileImage: data.user.profileImage
                }
              });
            } else {
              // Token invalid, clear storage
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(USER_KEY);
              setCurrentToken(null);
              setCurrentUser(null);
            }
          } catch (e) {
            console.warn('Token verification failed:', e);
          }
        }
      } catch (e) {
        console.error('Auth init error:', e);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Handle Google OAuth success
   * @param {string} credential - Google ID token
   */
  const signInWithGoogle = useCallback(async (credential) => {
    try {
      setError(null);
      setLoading(true);

      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }

      // Save to state and localStorage
      setCurrentToken(data.token);
      setCurrentUser(data.user);
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));

      // Fetch full user data with the new token (pass it directly since state may not be updated yet)
      await refreshUserDataWithToken(data.token);

      return data.user;
    } catch (e) {
      console.error('Google sign-in error:', e);
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Sign out user
   */
  const logout = useCallback(async () => {
    try {
      setError(null);
      
      // Clear local state
      setCurrentToken(null);
      setCurrentUser(null);
      setUserData(null);
      
      // Clear localStorage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem('userSubscription');
      localStorage.removeItem('userCredits');
      
    } catch (e) {
      console.error('Logout error:', e);
      setError(e.message);
    }
  }, []);

  /**
   * Refresh user data from backend
   */
  const refreshUserData = useCallback(async () => {
    const token = currentToken || localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    await refreshUserDataWithToken(token);
  }, [currentToken]);

  /**
   * Refresh user data with explicit token
   */
  const refreshUserDataWithToken = async (token) => {
    if (!token) return;

    try {
      setUserDataLoading(true);
      setUserDataError(null);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUserData({
          subscription: data.user.subscription,
          credits: data.user.z_credits,
          profile: {
            name: data.user.name,
            email: data.user.email,
            picture: data.user.picture,
            familyName: data.user.familyName,
            familyDescription: data.user.familyDescription,
            profileImage: data.user.profileImage
          }
        });
        
        // Update currentUser with latest data
        setCurrentUser(prev => ({
          ...prev,
          ...data.user
        }));
      }
    } catch (e) {
      console.error('Refresh user data error:', e);
      setUserDataError(e.message);
    } finally {
      setUserDataLoading(false);
    }
  };

  /**
   * Update user data locally
   */
  const updateUserData = useCallback((updates) => {
    setUserData(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Update quota after story generation
   */
  const updateQuotaAfterStoryGeneration = useCallback(async () => {
    await refreshUserData();
  }, [refreshUserData]);

  /**
   * Get current token
   */
  const getToken = useCallback(async () => {
    return currentToken;
  }, [currentToken]);

  // Set auth context for api.js
  useEffect(() => {
    setAuthContext({ currentToken, setCurrentToken });
  }, [currentToken]);

  const value = {
    // Auth state
    currentUser,
    currentToken,
    loading,
    error,
    
    // Auth methods
    signInWithGoogle,
    logout,
    getToken,
    
    // User data
    userData,
    userDataLoading,
    userDataError,
    refreshUserData,
    updateUserData,
    updateQuotaAfterStoryGeneration
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
